import React, { createContext, useContext, useState, ReactNode } from 'react';
import LocalStorage from '../../plugins/localStorage';
import ApiClient from '../../lib/api-client';
import { IUserDetailedDTO } from '@common-frontend-submodule/interfaces/user/iuser-detailed.interface';
import { UserRoutes } from '@common-frontend-submodule/enums/routes/user-routes.enum';
import { IGetUsersRequestDTO } from '@common-frontend-submodule/interfaces/user/iget-users-request.interface';
import { IUserCreationDTO } from '@common-frontend-submodule/interfaces/user/iuser-creation.interface';
import { IUserUpdateShortDTO } from '@common-frontend-submodule/interfaces/user/iuser-update-short.interface';
import { IUserUpdateDTO } from '@common-frontend-submodule/interfaces/user/iuser-update.interface';
import { IOfficeShortDTO } from '@common-frontend-submodule/interfaces/office/ioffice-short.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IUserDTO } from '@common-frontend-submodule/interfaces/user/iuser.interface';

const apiClient = ApiClient.getInstance()

interface UserState {
  currentUser: IUserDetailedDTO | null;
  currentOffice: IOfficeShortDTO | null;
  availableOffices: IOfficeShortDTO[];
}

interface UserContextProps extends UserState {
  setUserData: (user: IUserDetailedDTO) => void;
  getOwnUserInfo: () => Promise<IApiResponseGenericDTO<IUserDetailedDTO>>;
  updateOwnUserInfo: (model: IUserUpdateShortDTO) => Promise<IApiResponseGenericDTO<IUserDetailedDTO>>;
  getById: (userId: string) => Promise<IApiResponseGenericDTO<IUserDetailedDTO>>
  allOfficeUser: (id: string) => Promise<IApiResponseGenericDTO<IUserDTO[]>>;
  filter: (model: IGetUsersRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IUserDTO>>>
  createUser: (model: IUserCreationDTO) => Promise<IApiResponseGenericDTO<IUserDetailedDTO>>
  updateUser: (userId: string, model: IUserUpdateDTO) => Promise<IApiResponseGenericDTO<IUserDetailedDTO>>
  deleteUser: (userId: string) => Promise<IApiResponseGenericDTO<boolean>>
  CurrentOffice: () => IOfficeShortDTO | null
  CurrentUser: () => IUserDetailedDTO | null
  AvailableOffices: () => IOfficeShortDTO[]
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<IUserDetailedDTO | null>(LocalStorage.get('user', null));
  const [currentOffice, setCurrentOffice] = useState<IOfficeShortDTO | null>(LocalStorage.get('current-office', null));
  const [availableOffices, setAvailableOffices] = useState<IOfficeShortDTO[]>(LocalStorage.get('offices', []));

  const setUserData = (user: IUserDetailedDTO) => {
    const offices: IOfficeShortDTO[] = [];
    if (user.office) offices.push(user.office);
    for (const office of user.linkedOffices) {
      offices.push(office);
    }

    LocalStorage.set('user', user);
    setCurrentUser(user);

    LocalStorage.set('current-office', user.office);
    setCurrentOffice(user.office);

    LocalStorage.set('offices', offices);
    setAvailableOffices(offices);
  };

  const CurrentUser = (): IUserDetailedDTO | null => {
    if (!currentUser?.id) {
      const storedUser = LocalStorage.get('user')
      if (storedUser) setCurrentUser(storedUser)
    }
    return currentUser
  }

  const CurrentOffice = (): IOfficeShortDTO | null => {
    if (!currentOffice?.id) {
      const storedOffice = LocalStorage.get('current-office');
      if (storedOffice) setCurrentOffice(storedOffice);
    }
    return currentOffice;
  };

  const AvailableOffices = (): IOfficeShortDTO[] => {
    if (!availableOffices || !availableOffices.length) {
      const storedOffices = LocalStorage.get('offices')
      if (storedOffices) setAvailableOffices(storedOffices)
    }
    return availableOffices
  }

  const getOwnUserInfo = async (): Promise<IApiResponseGenericDTO<IUserDetailedDTO>> => {
    const result = await apiClient.getRequest<IUserDetailedDTO>(`${UserRoutes.BasePrefix}/${UserRoutes.Own}`);
    if (result.success) setUserData(result.data);
    return result;
  };

  const updateOwnUserInfo = async (model: IUserUpdateShortDTO): Promise<IApiResponseGenericDTO<IUserDetailedDTO>> => {
    const result = await apiClient.putRequest<IUserUpdateShortDTO, IUserDetailedDTO>(`${UserRoutes.BasePrefix}/${UserRoutes.Own}`, model);
    if (result.success) setUserData(result.data);
    return result;
  };

  const getById = async (userId: string): Promise<IApiResponseGenericDTO<IUserDetailedDTO>> => {
    return await apiClient.getRequest<IUserDetailedDTO>(`${UserRoutes.BasePrefix}/${userId}`)
  }

  const allOfficeUser = async (id: string) => {
    return await apiClient.getRequest<IUserDTO[]>(`${UserRoutes.BasePrefix}/all/${id}`);
  };

  const filter = async (model: IGetUsersRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IUserDTO>>> => {
    return await apiClient.postRequest<IGetUsersRequestDTO, IPaginatedResultDTO<IUserDTO>>(`${UserRoutes.BasePrefix}/${UserRoutes.List}`, model)
  }

  const createUser = async (model: IUserCreationDTO): Promise<IApiResponseGenericDTO<IUserDetailedDTO>> => {
    return await apiClient.postRequest<IUserCreationDTO, IUserDetailedDTO>(UserRoutes.BasePrefix, model)
  };

  const updateUser = async (userId: string, model: IUserUpdateDTO): Promise<IApiResponseGenericDTO<IUserDetailedDTO>> => {
    return await apiClient.putRequest<IUserUpdateDTO, IUserDetailedDTO>(`${UserRoutes.BasePrefix}/${userId}`, model)
  };

  const deleteUser = async (userId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${UserRoutes.BasePrefix}/${userId}`, null)
  }

  return (
    <UserContext.Provider value={{
      currentUser,
      currentOffice,
      availableOffices,
      setUserData,
      getOwnUserInfo,
      updateOwnUserInfo,
      getById,
      filter,
      allOfficeUser,
      createUser,
      updateUser,
      deleteUser,
      CurrentUser,
      CurrentOffice,
      AvailableOffices,
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserStore = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserStore must be used within a UserProvider');
  }
  return context;
};
